import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Maximize2, Loader2, Clock, MapPin } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function MyRosterWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    try {
      const data = await apiRequest('/roster/my-shifts?days=7');
      setShifts(data.shifts || data || []);
    } catch (error) {
      console.error('Failed to fetch roster');
    } finally {
      setLoading(false);
    }
  };

  const upcomingShifts = shifts.slice(0, 3);

  return (
    <Card data-testid="my-roster-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-green-500" />
            <CardTitle className="text-lg">My Roster</CardTitle>
          </div>
          {expandable && (
            <Button variant="ghost" size="icon" onClick={() => navigate(expandPath || '/roster')} className="h-8 w-8">
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : upcomingShifts.length === 0 ? (
          <div className="text-center py-4 text-slate-400">
            <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No upcoming shifts</p>
          </div>
        ) : (
          <div className="space-y-2">
            {upcomingShifts.map(shift => {
              const shiftDate = new Date(shift.date);
              const isToday = shiftDate.toDateString() === new Date().toDateString();
              
              return (
                <div key={shift.id} className={`p-3 rounded border ${isToday ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-slate-900">
                        {shiftDate.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="h-3 w-3 text-slate-400" />
                        <span className="text-xs text-slate-500">{shift.start_time} - {shift.end_time}</span>
                      </div>
                      {shift.store && (
                        <div className="flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3 text-slate-400" />
                          <span className="text-xs text-slate-500">{shift.store}</span>
                        </div>
                      )}
                    </div>
                    {isToday && <Badge className="bg-green-100 text-green-700">Today</Badge>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
